import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/Login";
import { Plus, Pencil, Trash2 } from "lucide-react";

/* Journeys */
export function JourneysManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/journeys").then(({ data }) => setItems(data.journeys || []));
  useEffect(() => { load(); }, []);

  const empty = {
    key: "", title: "", description: "", cover_image: "",
    age_groups: [], duration_days: 7, order: 0, featured: false,
    premium: false, status: "publicado", steps: [],
  };
  const remove = async (key) => {
    if (!window.confirm("Arquivar esta jornada?")) return;
    await api.delete(`/admin/journeys/${key}`);
    await load();
    toast.success("Jornada arquivada");
  };

  return (
    <div data-testid="admin-journeys-manager">
      <div className="flex items-center justify-between">
        <div><h2 className="text-xl font-extrabold text-[#243B53]">Jornadas</h2>
             <p className="text-sm text-[#243B53]/60">{items.length} totais</p></div>
        <button data-testid="admin-journey-new-btn" onClick={() => setEditing(empty)} className="btn-primary text-sm inline-flex items-center gap-2">
          <Plus size={16}/> Nova jornada
        </button>
      </div>
      <div className="mt-4 card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFF9ED] text-left">
            <tr><th className="p-3">Título</th><th className="p-3">Chave</th><th className="p-3">Status</th><th className="p-3">Etapas</th><th className="p-3">Prem.</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((j) => (
              <tr key={j.key} className="border-t border-[#243B53]/5" data-testid={`admin-journey-row-${j.key}`}>
                <td className="p-3 font-bold">{j.title}</td>
                <td className="p-3 text-xs">{j.key}</td>
                <td className="p-3"><span className="chip capitalize">{j.status}</span></td>
                <td className="p-3">{(j.steps || []).length}</td>
                <td className="p-3">{j.premium ? "🔒" : "—"}</td>
                <td className="p-3 flex gap-2">
                  <button data-testid={`admin-journey-edit-${j.key}`} onClick={() => setEditing(j)} className="btn-outline text-xs inline-flex items-center gap-1"><Pencil size={14}/>Editar</button>
                  <button data-testid={`admin-journey-delete-${j.key}`} onClick={() => remove(j.key)} className="btn-outline text-xs inline-flex items-center gap-1"><Trash2 size={14}/>Arquivar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <JourneyEditor initial={editing} onClose={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function JourneyEditor({ initial, onClose }) {
  const [form, setForm] = useState({ ...initial });
  const setField = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const isNew = !initial?.updated_at;

  const setStep = (i, patch) => setForm((s) => ({ ...s, steps: s.steps.map((st, idx) => idx === i ? { ...st, ...patch } : st) }));
  const addStep = () => setForm((s) => ({ ...s, steps: [...(s.steps || []), { step_number: (s.steps?.length || 0) + 1, title: "", description: "", content_id: "", kind: "atividade", points_awarded: 10, stars_awarded: 1 }] }));
  const rmStep = (i) => setForm((s) => ({ ...s, steps: s.steps.filter((_, idx) => idx !== i).map((st, idx) => ({ ...st, step_number: idx + 1 })) }));

  const save = async () => {
    try {
      const body = { ...form, duration_days: Number(form.duration_days || 7), order: Number(form.order || 0),
                     age_groups: Array.isArray(form.age_groups) ? form.age_groups : (form.age_groups || "").split(",").map(x=>x.trim()).filter(Boolean) };
      if (isNew) await api.post("/admin/journeys", body);
      else await api.put(`/admin/journeys/${form.key}`, body);
      toast.success("Jornada salva");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" data-testid="admin-journey-editor">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-6">
        <h3 className="font-extrabold text-[#243B53] text-xl">{isNew ? "Nova jornada" : `Editar: ${form.title}`}</h3>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Chave (única)" value={form.key} onChange={setField("key")} testid="journey-key" />
          <Field label="Título" value={form.title} onChange={setField("title")} testid="journey-title" />
          <div className="sm:col-span-2"><Field label="Descrição" value={form.description} onChange={setField("description")} as="textarea" testid="journey-desc" /></div>
          <Field label="URL da capa" value={form.cover_image} onChange={setField("cover_image")} testid="journey-cover" />
          <Field label="Duração (dias)" type="number" value={form.duration_days} onChange={setField("duration_days")} testid="journey-duration" />
          <Field label="Ordem" type="number" value={form.order} onChange={setField("order")} testid="journey-order" />
          <Field label="Faixas etárias (vírgulas)" value={Array.isArray(form.age_groups)?form.age_groups.join(","):form.age_groups} onChange={setField("age_groups")} testid="journey-ages" />
          <Field label="Status" as="select" value={form.status} onChange={setField("status")} testid="journey-status"
                 options={[{value:"publicado",label:"Publicado"},{value:"rascunho",label:"Rascunho"},{value:"arquivado",label:"Arquivado"}]} />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!form.featured} onChange={(e)=>setField("featured")(e.target.checked)} data-testid="journey-featured"/> Destaque</label>
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!form.premium} onChange={(e)=>setField("premium")(e.target.checked)} data-testid="journey-premium"/> Premium</label>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h4 className="font-extrabold text-[#243B53]">Etapas</h4>
            <button data-testid="journey-add-step" onClick={addStep} className="btn-outline text-xs">+ Etapa</button>
          </div>
          <div className="mt-3 space-y-3">
            {(form.steps || []).map((s, i) => (
              <div key={i} className="card-soft p-3 space-y-2" data-testid={`journey-step-editor-${i}`}>
                <div className="flex justify-between text-xs font-bold text-[#243B53]/60">
                  Etapa #{s.step_number}
                  <button onClick={() => rmStep(i)} className="text-red-500">Remover</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Field label="Título" value={s.title} onChange={(v) => setStep(i, {title: v})} />
                  <Field label="ID do conteúdo (opcional)" value={s.content_id || ""} onChange={(v) => setStep(i, {content_id: v || null})} />
                  <Field label="Descrição" value={s.description} onChange={(v) => setStep(i, {description: v})} />
                  <Field label="Tipo" as="select" value={s.kind} onChange={(v) => setStep(i, {kind: v})}
                         options={[{value:"historia",label:"História"},{value:"atividade",label:"Atividade"},{value:"versiculo",label:"Versículo"},{value:"oracao",label:"Oração"},{value:"familia",label:"Família"}]} />
                  <Field label="Pontos" type="number" value={s.points_awarded} onChange={(v) => setStep(i, {points_awarded: Number(v)||0})} />
                  <Field label="Estrelas" type="number" value={s.stars_awarded} onChange={(v) => setStep(i, {stars_awarded: Number(v)||0})} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline">Cancelar</button>
          <button data-testid="journey-save-btn" onClick={save} className="btn-primary">Salvar</button>
        </div>
      </div>
    </div>
  );
}

/* Missions */
export function MissionsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/missions").then(({ data }) => setItems(data.missions || []));
  useEffect(() => { load(); }, []);
  const empty = { key: "", title: "", description: "", icon: "🎯", kind: "custom",
                  target_content_id: null, points_reward: 10, stars_reward: 1, active: true, order: 0 };
  const remove = async (key) => {
    if (!window.confirm("Desativar esta missão?")) return;
    await api.delete(`/admin/missions/${key}`);
    await load();
    toast.success("Missão desativada");
  };

  return (
    <div data-testid="admin-missions-manager">
      <div className="flex items-center justify-between">
        <div><h2 className="text-xl font-extrabold text-[#243B53]">Missões</h2>
             <p className="text-sm text-[#243B53]/60">{items.length} totais</p></div>
        <button data-testid="admin-mission-new-btn" onClick={() => setEditing(empty)} className="btn-primary text-sm inline-flex items-center gap-2">
          <Plus size={16}/> Nova missão
        </button>
      </div>
      <div className="mt-4 card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFF9ED] text-left"><tr>
            <th className="p-3">Ícone</th><th className="p-3">Título</th><th className="p-3">Chave</th><th className="p-3">Tipo</th><th className="p-3">Pontos</th><th className="p-3">Ativa</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.key} className="border-t border-[#243B53]/5" data-testid={`admin-mission-row-${m.key}`}>
                <td className="p-3 text-xl">{m.icon}</td>
                <td className="p-3 font-bold">{m.title}</td>
                <td className="p-3 text-xs">{m.key}</td>
                <td className="p-3"><span className="chip capitalize">{m.kind}</span></td>
                <td className="p-3">{m.points_reward}</td>
                <td className="p-3">{m.active ? "✅" : "❌"}</td>
                <td className="p-3 flex gap-2">
                  <button data-testid={`admin-mission-edit-${m.key}`} onClick={() => setEditing(m)} className="btn-outline text-xs">Editar</button>
                  <button data-testid={`admin-mission-delete-${m.key}`} onClick={() => remove(m.key)} className="btn-outline text-xs">Desativar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <MissionEditor initial={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function MissionEditor({ initial, onClose }) {
  const [form, setForm] = useState({ ...initial });
  const setField = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const isNew = !initial?.created_at;
  const save = async () => {
    try {
      const body = { ...form, points_reward: Number(form.points_reward)||0, stars_reward: Number(form.stars_reward)||0,
                     order: Number(form.order)||0, target_content_id: form.target_content_id || null };
      if (isNew) await api.post("/admin/missions", body);
      else await api.put(`/admin/missions/${form.key}`, body);
      toast.success("Missão salva");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" data-testid="admin-mission-editor">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[92vh] overflow-y-auto p-6">
        <h3 className="font-extrabold text-[#243B53] text-xl">{isNew ? "Nova missão" : `Editar: ${form.title}`}</h3>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Chave (única)" value={form.key} onChange={setField("key")} />
          <Field label="Ícone" value={form.icon} onChange={setField("icon")} />
          <div className="sm:col-span-2"><Field label="Título" value={form.title} onChange={setField("title")} /></div>
          <div className="sm:col-span-2"><Field label="Descrição" value={form.description} onChange={setField("description")} as="textarea" /></div>
          <Field label="Tipo" as="select" value={form.kind} onChange={setField("kind")}
                 options={[{value:"watch",label:"Assistir"},{value:"activity",label:"Atividade"},{value:"family_moment",label:"Família"},{value:"streak",label:"Sequência"},{value:"custom",label:"Custom"}]} />
          <Field label="ID conteúdo alvo (opcional)" value={form.target_content_id || ""} onChange={setField("target_content_id")} />
          <Field label="Pontos" type="number" value={form.points_reward} onChange={setField("points_reward")} />
          <Field label="Estrelas" type="number" value={form.stars_reward} onChange={setField("stars_reward")} />
          <Field label="Ordem" type="number" value={form.order} onChange={setField("order")} />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!form.active} onChange={(e)=>setField("active")(e.target.checked)}/> Ativa</label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline">Cancelar</button>
          <button data-testid="mission-save-btn" onClick={save} className="btn-primary">Salvar</button>
        </div>
      </div>
    </div>
  );
}
